import React, { useState } from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, FileTextIcon, BuildingIcon, ShieldIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
interface ConstructionPaymentReportProps {
  filters: {
    dateRange: string;
    startDate: string;
    endDate: string;
  };
  locale?: string;
}
export function ConstructionPaymentReport({
  filters,
  locale = 'en-US'
}: ConstructionPaymentReportProps) {
  const [selectedTab, setSelectedTab] = useState('overview');
  // Sample data for Security of Payment
  const sopClaims = [{
    id: 'SOP-2023-001',
    claimNumber: 'PC-001',
    contractor: 'BuildCorp Pty Ltd',
    project: 'City Tower Development',
    claimDate: '2023-11-01',
    dueDate: '2023-11-15',
    amount: 450000,
    status: 'Payment Due',
    daysRemaining: 5,
    retentionAmount: 22500,
    progressPercentage: 45
  }, {
    id: 'SOP-2023-002',
    claimNumber: 'PC-002',
    contractor: 'Steel Solutions Ltd',
    project: 'Industrial Complex',
    claimDate: '2023-10-28',
    dueDate: '2023-11-12',
    amount: 280000,
    status: 'Under Review',
    daysRemaining: 2,
    retentionAmount: 14000,
    progressPercentage: 60
  }, {
    id: 'SOP-2023-003',
    claimNumber: 'PC-003',
    contractor: 'Electrical Contractors Inc',
    project: 'Retail Plaza',
    claimDate: '2023-10-25',
    dueDate: '2023-11-08',
    amount: 125000,
    status: 'Overdue',
    daysRemaining: -2,
    retentionAmount: 6250,
    progressPercentage: 75
  }, {
    id: 'SOP-2023-004',
    claimNumber: 'PC-004',
    contractor: 'Plumbing Services Co',
    project: 'Residential Complex',
    claimDate: '2023-11-05',
    dueDate: '2023-11-20',
    amount: 95000,
    status: 'Approved',
    daysRemaining: 10,
    retentionAmount: 4750,
    progressPercentage: 30
  }];
  // Calculate summary statistics
  const totalClaimValue = sopClaims.reduce((sum, claim) => sum + claim.amount, 0);
  const totalRetention = sopClaims.reduce((sum, claim) => sum + claim.retentionAmount, 0);
  const overdueClaims = sopClaims.filter(claim => claim.daysRemaining < 0).length;
  const upcomingPayments = sopClaims.filter(claim => claim.daysRemaining > 0 && claim.daysRemaining <= 7).length;
  // Project summary data
  const projectSummary = [{
    project: 'City Tower Development',
    contractValue: 10000000,
    claimedToDate: 4500000,
    paidToDate: 4050000,
    retention: 225000,
    progressPercentage: 45
  }, {
    project: 'Industrial Complex',
    contractValue: 5500000,
    claimedToDate: 3300000,
    paidToDate: 2970000,
    retention: 165000,
    progressPercentage: 60
  }, {
    project: 'Retail Plaza',
    contractValue: 3200000,
    claimedToDate: 2400000,
    paidToDate: 2160000,
    retention: 120000,
    progressPercentage: 75
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Payment Due':
        return 'bg-blue-100 text-blue-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-15605">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-15606">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15607">
          <div className="p-5" data-id="element-15608">
            <div className="flex items-center" data-id="element-15609">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-15610">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15611" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15612">
                <dl data-id="element-15613">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15614">
                    Total Claim Value
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15615">
                    {formatCurrency(totalClaimValue, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15616">
          <div className="p-5" data-id="element-15617">
            <div className="flex items-center" data-id="element-15618">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-15619">
                <ShieldIcon className="h-6 w-6 text-white" data-id="element-15620" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15621">
                <dl data-id="element-15622">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15623">
                    Total Retention
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15624">
                    {formatCurrency(totalRetention, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15625">
          <div className="p-5" data-id="element-15626">
            <div className="flex items-center" data-id="element-15627">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-3" data-id="element-15628">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-15629" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15630">
                <dl data-id="element-15631">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15632">
                    Overdue Claims
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15633">
                    {overdueClaims}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15634">
          <div className="p-5" data-id="element-15635">
            <div className="flex items-center" data-id="element-15636">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15637">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-15638" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15639">
                <dl data-id="element-15640">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15641">
                    Due This Week
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15642">
                    {upcomingPayments}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white shadow rounded-lg" data-id="element-15643">
        <div className="border-b border-gray-200" data-id="element-15644">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-15645">
            <button onClick={() => setSelectedTab('overview')} className={`${selectedTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15646">
              Security of Payment
            </button>
            <button onClick={() => setSelectedTab('projects')} className={`${selectedTab === 'projects' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15647">
              Project Summary
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-15648">
          {selectedTab === 'overview' && <div className="space-y-6" data-id="element-15649">
              <div className="flex justify-between items-center" data-id="element-15650">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-15651">
                  Security of Payment Claims
                </h3>
                <div className="text-sm text-gray-500" data-id="element-15652">
                  Showing {sopClaims.length} active claims
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-15653">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-15654">
                  <thead className="bg-gray-50" data-id="element-15655">
                    <tr data-id="element-15656">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15657">
                        Claim #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15658">
                        Contractor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15659">
                        Project
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15660">
                        Claim Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15661">
                        Retention
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15662">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15663">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15664">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-15665">
                    {sopClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50" data-id="element-15666">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-15667">
                          {claim.claimNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15668">
                          {claim.contractor}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15669">
                          {claim.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15670">
                          {formatCurrency(claim.amount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15671">
                          {formatCurrency(claim.retentionAmount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15672">
                          <div className="flex items-center" data-id="element-15673">
                            <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-15674" />
                            {new Date(claim.dueDate).toLocaleDateString(locale)}
                            {claim.daysRemaining < 0 && <span className="ml-2 text-red-600 font-medium" data-id="element-15675">
                                ({Math.abs(claim.daysRemaining)} days overdue)
                              </span>}
                            {claim.daysRemaining >= 0 && claim.daysRemaining <= 7 && <span className="ml-2 text-yellow-600 font-medium" data-id="element-15676">
                                  ({claim.daysRemaining} days)
                                </span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15677">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(claim.status)}`} data-id="element-15678">
                            {claim.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15679">
                          <div className="flex items-center" data-id="element-15680">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2" data-id="element-15681">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                          width: `${claim.progressPercentage}%`
                        }} data-id="element-15682"></div>
                            </div>
                            <span className="text-sm text-gray-500" data-id="element-15683">
                              {claim.progressPercentage}%
                            </span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {selectedTab === 'projects' && <div className="space-y-6" data-id="element-15684">
              <div className="flex justify-between items-center" data-id="element-15685">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-15686">
                  Project Financial Summary
                </h3>
              </div>
              <div className="space-y-4" data-id="element-15687">
                {projectSummary.map((project, index) => <div key={index} className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-15688">
                    <div className="flex justify-between items-start" data-id="element-15689">
                      <div data-id="element-15690">
                        <h4 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-15691">
                          <BuildingIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-15692" />
                          {project.project}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-15693">
                          Contract Value:{' '}
                          {formatCurrency(project.contractValue, 'USD', locale)}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-15694">
                        <div className="text-2xl font-bold text-gray-900" data-id="element-15695">
                          {project.progressPercentage}%
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-15696">Complete</div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-15697">
                      <div className="bg-indigo-600 h-3 rounded-full" style={{
                  width: `${project.progressPercentage}%`
                }} data-id="element-15698"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-15699">
                      <div data-id="element-15700">
                        <div className="text-sm text-gray-500" data-id="element-15701">
                          Claimed to Date
                        </div>
                        <div className="text-lg font-semibold text-gray-900" data-id="element-15702">
                          {formatCurrency(project.claimedToDate, 'USD', locale)}
                        </div>
                      </div>
                      <div data-id="element-15703">
                        <div className="text-sm text-gray-500" data-id="element-15704">
                          Paid to Date
                        </div>
                        <div className="text-lg font-semibold text-gray-900" data-id="element-15705">
                          {formatCurrency(project.paidToDate, 'USD', locale)}
                        </div>
                      </div>
                      <div data-id="element-15706">
                        <div className="text-sm text-gray-500" data-id="element-15707">
                          Retention Held
                        </div>
                        <div className="text-lg font-semibold text-yellow-600" data-id="element-15708">
                          {formatCurrency(project.retention, 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}